import {
  FiAlertTriangle,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiCreditCard,
  FiDownload,
  FiFileText,
  FiGift,
  FiPlus,
  FiShoppingBag,
  FiTrendingUp,
  FiBriefcase,
} from "react-icons/fi";
import { Link } from "react-router-dom";
import {
  DashboardPage,
  EmptyState,
  ListRow,
  Pill,
  QuickActions,
  SectionCard,
  StatTile,
  TextLink,
} from "../../../components/Dashboard";
import TransactionRow from "./components/TransactionRow";
import {
  borrowingNote,
  buildLoanRows,
  buildNextPayment,
  formatNaira,
  greetingFor,
} from "./memberHomeModel";

const ROUTES = {
  repay: "/member/loan_management/loan_repayment",
  apply: "/member/loan_management/application",
  loanStatus: "/member/loan_management/loan_status",
  contribute: "/member/contribution/record",
  contributions: "/member/contribution",
  history: "/member/transaction_history",
  birthdays: "/member/birthdays",
};

const QUICK_ACTIONS = [
  { label: "Contribute", to: ROUTES.contribute, icon: FiPlus },
  // Onboarding tour points at this element (see tourSteps.js, TOUR_SELECTORS.loanCta)
  { label: "Apply for a loan", to: ROUTES.apply, icon: FiFileText, tour: "tour-loan-cta" },
  { label: "Withdraw savings", to: ROUTES.contributions, icon: FiDownload },
  { label: "Statement", to: ROUTES.history, icon: FiTrendingUp },
];

function NextPaymentCard({ payment }) {
  if (payment.kind === "none") {
    return (
      <section className="dx-hero dx-hero--none dx-o-2" aria-label="Next payment">
        <div className="dx-hero-label">
          <span>Next payment</span>
          <Pill tone="ok">
            <FiCheckCircle aria-hidden="true" size={13} />
            All clear
          </Pill>
        </div>
        <div className="dx-hero-amount" style={{ fontSize: 24 }}>
          You have no payments due
        </div>
        <div className="dx-hero-sub" style={{ color: "#62626a" }}>
          Nothing is owed on any loan right now.
        </div>
      </section>
    );
  }

  const overdue = payment.kind === "overdue";
  const subject = overdue
    ? `${payment.overdueCount || 1} overdue ${payment.overdueCount > 1 ? "repayments" : "repayment"}`
    : [payment.loanTypeName, payment.dueDateLabel && `due ${payment.dueDateLabel}`].filter(Boolean).join(", ");

  return (
    <section
      className={`dx-hero${overdue ? " dx-hero--overdue" : ""} dx-o-2`}
      aria-label={overdue ? "Overdue repayment" : "Next payment"}
    >
      <div className="dx-hero-label">
        <span>{overdue ? "Overdue" : "Next payment"}</span>
        {payment.daysLabel ? (
          <Pill tone="warn">
            {overdue ? <FiAlertTriangle aria-hidden="true" size={13} /> : <FiClock aria-hidden="true" size={13} />}
            {payment.daysLabel}
          </Pill>
        ) : null}
      </div>
      <div className="dx-hero-amount">{formatNaira(payment.amount)}</div>
      {subject ? <div className="dx-hero-sub">{subject}</div> : null}
      <div className="dx-hero-foot">
        <Link to={ROUTES.repay} className="dx-btn dx-btn--light">
          <FiCreditCard aria-hidden="true" size={18} />
          Pay now
        </Link>
      </div>
    </section>
  );
}

/**
 * The member home page. Presentational: everything arrives as props so it can be tested
 * and previewed without a store or a network.
 */
export default function MemberHomeView({
  dashboard,
  firstName,
  birthdaysToday = 0,
  capabilities = {},
  activity = null,
  now = new Date(),
}) {
  const payment = buildNextPayment(dashboard.repayments, now);
  const loanRows = buildLoanRows(dashboard.loans);
  const summary = dashboard.summary || {};
  const borrowing = dashboard.borrowing;
  const transactions = (dashboard.recentTransactions || []).slice(0, 5);
  const member = dashboard.member || {};
  const detail = [member.member_no && `Member ${member.member_no}`, member.branch_name].filter(Boolean).join(" · ");
  const { investment, marketplace, realEstate } = capabilities;
  const hasDiscover = investment || marketplace || realEstate;

  return (
    <DashboardPage>
      <div className="dx-page">
        <div className="dx-greet dx-full dx-o-1">
          <div className="dx-greet-row">
            <div>
              <h1>
                {greetingFor(now)}
                {firstName ? `, ${firstName}` : ""}
              </h1>
              {detail ? <p>{detail}</p> : null}
            </div>
            {birthdaysToday > 0 ? (
              <Link to={ROUTES.birthdays} className="dx-chip">
                <FiGift aria-hidden="true" size={16} />
                {birthdaysToday} {birthdaysToday === 1 ? "birthday" : "birthdays"} today
              </Link>
            ) : null}
          </div>
        </div>

        <div className="dx-col">
          <NextPaymentCard payment={payment} />

          <div className="dx-tiles dx-o-5" aria-label="Your money">
            <StatTile label="Savings" value={formatNaira(summary.savings)} to={ROUTES.contributions} />
            <StatTile label="Shares" value={formatNaira(summary.shares)} to={ROUTES.contributions} />
            <StatTile
              label="Total contributed"
              value={formatNaira(summary.total_contribution)}
              to={ROUTES.contributions}
            />
          </div>

          <SectionCard
            title="Recent transactions"
            className="dx-o-7"
            action={transactions.length > 0 ? <TextLink to={ROUTES.history}>View all</TextLink> : null}
          >
            {transactions.length > 0 ? (
              <div>
                {transactions.map((transaction, index) => (
                  <TransactionRow
                    key={transaction?.id || index}
                    transaction={transaction}
                    isLast={index === transactions.length - 1}
                  />
                ))}
              </div>
            ) : (
              <EmptyState
                icon={FiBriefcase}
                title="No transactions yet"
                action={
                  <Link to={ROUTES.contribute} className="dx-link">
                    Record your first contribution
                  </Link>
                }
              >
                Your contributions and repayments will show here.
              </EmptyState>
            )}
          </SectionCard>

          {activity ? (
            <SectionCard title="Updates" className="dx-o-8">
              {activity}
            </SectionCard>
          ) : null}
        </div>

        <div className="dx-col">
          <div className="dx-o-3" aria-label="Quick actions">
            <QuickActions actions={QUICK_ACTIONS} />
          </div>

          {borrowing && Number(borrowing.maximum_borrowing_power) >= 0 ? (
            <SectionCard className="dx-o-4" aria-label="Borrowing power">
              <div>
                <span className="dx-tile-label">You can borrow up to</span>
                <div className="dx-hero-amount" style={{ fontSize: 26 }}>
                  {formatNaira(borrowing.maximum_borrowing_power)}
                </div>
                <div className="dx-tile-note">{borrowingNote(borrowing)}</div>
              </div>
              <Link to={ROUTES.apply} className="dx-btn dx-btn--outline">
                Apply for a loan
              </Link>
            </SectionCard>
          ) : null}

          <SectionCard
            title="Your loans"
            className="dx-o-6"
            action={<TextLink to={ROUTES.loanStatus}>Details</TextLink>}
          >
            <div className="dx-list">
              {loanRows.map((row) => (
                <ListRow
                  key={row.key}
                  to={ROUTES.loanStatus}
                  icon={row.key === "pending" ? FiClock : row.key === "approved" ? FiCheckCircle : FiBriefcase}
                  tone={row.tone}
                  title={row.title}
                  subtitle={row.subtitle}
                  end={row.count}
                />
              ))}
            </div>
          </SectionCard>

          {hasDiscover ? (
            <SectionCard title="More from your cooperative" className="dx-o-9">
              <div className="dx-discover">
                {investment ? (
                  <Link to="/invest/investments" className="dx-chip">
                    <FiTrendingUp aria-hidden="true" size={16} />
                    Investments
                  </Link>
                ) : null}
                {marketplace ? (
                  <Link to="/invest/marketplace" className="dx-chip">
                    <FiShoppingBag aria-hidden="true" size={16} />
                    Marketplace
                  </Link>
                ) : null}
                {realEstate ? (
                  <Link to="/invest/requests?filter=real_estate" className="dx-chip">
                    <FiCalendar aria-hidden="true" size={16} />
                    Property requests
                  </Link>
                ) : null}
              </div>
            </SectionCard>
          ) : null}
        </div>
      </div>
    </DashboardPage>
  );
}
